import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { api } from '@/App';
import { 
  Search, 
  Filter, 
  Download, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  Calendar,
  User,
  Activity,
  FileText,
  Eye,
  Clock,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  BarChart3,
  Users,
  TrendingUp,
  Layers
} from 'lucide-react';

// Action type badges with colors
const ACTION_BADGES = {
  CREATE: { color: 'bg-green-100 text-green-700', icon: '➕' },
  UPDATE: { color: 'bg-blue-100 text-blue-700', icon: '✏️' },
  DELETE: { color: 'bg-red-100 text-red-700', icon: '🗑️' },
  LOGIN: { color: 'bg-purple-100 text-purple-700', icon: '🔐' },
  LOGOUT: { color: 'bg-gray-100 text-gray-700', icon: '🚪' },
  LOGIN_FAILED: { color: 'bg-red-100 text-red-700', icon: '⚠️' },
  APPROVE: { color: 'bg-emerald-100 text-emerald-700', icon: '✅' },
  REJECT: { color: 'bg-orange-100 text-orange-700', icon: '❌' },
  CHECKIN: { color: 'bg-cyan-100 text-cyan-700', icon: '📥' },
  CHECKOUT: { color: 'bg-amber-100 text-amber-700', icon: '📤' },
  PAYMENT: { color: 'bg-green-100 text-green-700', icon: '💰' },
  CANCEL: { color: 'bg-red-100 text-red-700', icon: '🚫' },
  ENABLE: { color: 'bg-green-100 text-green-700', icon: '✓' },
  DISABLE: { color: 'bg-gray-100 text-gray-700', icon: '⊘' },
  SETTINGS_CHANGE: { color: 'bg-indigo-100 text-indigo-700', icon: '⚙️' },
  PASSWORD_CHANGE: { color: 'bg-yellow-100 text-yellow-700', icon: '🔑' },
  ASSIGN: { color: 'bg-blue-100 text-blue-700', icon: '👤' },
  UPLOAD: { color: 'bg-violet-100 text-violet-700', icon: '📁' },
};

const STATUS_BADGES = {
  success: { color: 'bg-green-100 text-green-700', label: 'Success' },
  failure: { color: 'bg-red-100 text-red-700', label: 'Failed' },
  warning: { color: 'bg-yellow-100 text-yellow-700', label: 'Warning' }
};

export default function AuditTrail({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(25);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    action: '',
    entity_type: '',
    user_email: '',
    start_date: '',
    end_date: '',
    status: ''
  });
  
  // Available filter options (fetched from API)
  const [availableActions, setAvailableActions] = useState([]);
  const [availableEntityTypes, setAvailableEntityTypes] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  
  // Selected log for detail view
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('logs');

  // Fetch audit logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });
      
      if (filters.search) params.append('search', filters.search);
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.status) params.append('status', filters.status);
      
      const response = await api.get(`/audit/logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await api.get('/audit/stats?days=7');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const [actionsRes, entityTypesRes, usersRes] = await Promise.all([
        api.get('/audit/actions'),
        api.get('/audit/entity-types'),
        api.get('/audit/users')
      ]);
      setAvailableActions(actionsRes.data.actions || []);
      setAvailableEntityTypes(entityTypesRes.data.entity_types || []);
      setAvailableUsers(usersRes.data.users || []);
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, [fetchStats, fetchFilterOptions]);

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      search: '',
      action: '',
      entity_type: '',
      user_email: '',
      start_date: '',
      end_date: '',
      status: ''
    });
    setPage(1);
  };

  // Export logs
  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (filters.search) params.append('search', filters.search);
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await api.get(`/audit/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export logs');
    }
  };

  // View log details
  const viewLogDetails = async (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Render stats cards
  const renderStatsCards = () => {
    if (statsLoading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      );
    }

    if (!stats) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Total Events (7d)</p>
                <p className="text-2xl font-bold">{stats.total_logs?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Failed Actions</p>
                <p className="text-2xl font-bold text-red-600">{stats.failed_actions || 0}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Active Users</p>
                <p className="text-2xl font-bold">{stats.most_active_users?.length || 0}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Entity Types</p>
                <p className="text-2xl font-bold">{stats.logs_by_entity?.length || 0}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Layers className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render log detail dialog
  const renderDetailDialog = () => (
    <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Audit Log Details
          </DialogTitle>
          <DialogDescription>
            {selectedLog?.id}
          </DialogDescription>
        </DialogHeader>
        
        {selectedLog && (
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Timestamp</Label>
                  <p className="font-medium">{formatTimestamp(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Status</Label>
                  <Badge className={STATUS_BADGES[selectedLog.status]?.color || 'bg-gray-100'}>
                    {selectedLog.status || 'success'}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">Action</Label>
                  <Badge className={ACTION_BADGES[selectedLog.action]?.color || 'bg-gray-100'}>
                    {ACTION_BADGES[selectedLog.action]?.icon} {selectedLog.action}
                  </Badge>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Entity Type</Label>
                  <p className="font-medium">{selectedLog.entity_type || '-'}</p>
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-500">Description</Label>
                <p className="text-sm bg-gray-50 p-2 rounded">{selectedLog.description || '-'}</p>
              </div>

              {/* User Info */}
              <div className="border-t pt-4">
                <Label className="text-xs text-gray-500 block mb-2">User Information</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-400">Email</Label>
                    <p className="text-sm">{selectedLog.user_email || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-400">Role</Label>
                    <p className="text-sm">{selectedLog.user_role || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Entity Info */}
              {selectedLog.entity_id && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-gray-500 block mb-2">Entity Information</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-400">Entity ID</Label>
                      <p className="text-sm font-mono text-xs">{selectedLog.entity_id}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-400">Entity Name</Label>
                      <p className="text-sm">{selectedLog.entity_name || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Changes */}
              {selectedLog.changes && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-gray-500 block mb-2">Changes</Label>
                  <div className="bg-gray-50 rounded p-3 overflow-x-auto">
                    <pre className="text-xs">{JSON.stringify(selectedLog.changes, null, 2)}</pre>
                  </div>
                </div>
              )}

              {/* Technical Info */}
              {(selectedLog.ip_address || selectedLog.user_agent) && (
                <div className="border-t pt-4">
                  <Label className="text-xs text-gray-500 block mb-2">Technical Details</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {selectedLog.ip_address && (
                      <div>
                        <Label className="text-xs text-gray-400">IP Address</Label>
                        <p className="text-sm font-mono">{selectedLog.ip_address}</p>
                      </div>
                    )}
                    {selectedLog.user_agent && (
                      <div>
                        <Label className="text-xs text-gray-400">User Agent</Label>
                        <p className="text-xs text-gray-600 break-all">{selectedLog.user_agent}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6" data-testid="audit-trail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-7 w-7 text-indigo-600" />
            Audit Trail
          </h1>
          <p className="text-gray-500">Monitor and track all system activities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport('json')}>
            <Download className="h-4 w-4 mr-1" />
            JSON
          </Button>
          <Button variant="outline" size="sm" onClick={() => { fetchLogs(); fetchStats(); }}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {renderStatsCards()}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search logs..."
                  className="pl-9"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  data-testid="audit-search-input"
                />
              </div>
            </div>

            {/* Action Filter */}
            <div>
              <Label className="text-xs">Action</Label>
              <Select value={filters.action || 'all'} onValueChange={(v) => handleFilterChange('action', v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="audit-action-filter">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {availableActions.map(action => (
                    <SelectItem key={action} value={action}>{action}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Entity Type Filter */}
            <div>
              <Label className="text-xs">Entity Type</Label>
              <Select value={filters.entity_type || 'all'} onValueChange={(v) => handleFilterChange('entity_type', v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="audit-entity-filter">
                  <SelectValue placeholder="All Entities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Entities</SelectItem>
                  {availableEntityTypes.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div>
              <Label className="text-xs">From Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
                data-testid="audit-start-date"
              />
            </div>

            {/* End Date */}
            <div>
              <Label className="text-xs">To Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
                data-testid="audit-end-date"
              />
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full">
                <XCircle className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Activity Logs</CardTitle>
            <Badge variant="outline">{total.toLocaleString()} records</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No audit logs found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="w-[180px]">Timestamp</TableHead>
                      <TableHead className="w-[100px]">Action</TableHead>
                      <TableHead className="w-[120px]">Entity</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="w-[180px]">User</TableHead>
                      <TableHead className="w-[80px]">Status</TableHead>
                      <TableHead className="w-[60px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} className="hover:bg-gray-50">
                        <TableCell className="text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTimestamp(log.timestamp)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-xs ${ACTION_BADGES[log.action]?.color || 'bg-gray-100'}`}>
                            {ACTION_BADGES[log.action]?.icon} {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{log.entity_type || '-'}</TableCell>
                        <TableCell className="text-sm max-w-[300px] truncate" title={log.description}>
                          {log.description || '-'}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-gray-400" />
                            <span className="truncate max-w-[150px]" title={log.user_email}>
                              {log.user_email || 'System'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.status === 'failure' ? (
                            <XCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => viewLogDetails(log)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">
                  Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total.toLocaleString()} entries
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      {renderDetailDialog()}
    </div>
  );
}
